import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'

/**
 * 3-D Secure strategy of the processor (Adyen and Checkout.com). Mirrors
 * `ThreeDSMode` in `adyen/threeds-handler.ts`. `sca_only` is the default: 3DS is attempted only where PSD2/SCA
 * mandates it (EEA + UK) and never for the US / other non-SCA regions — forcing
 * a challenge there just costs conversion (the July 2026 US decline incident).
 */
export type ThreeDSMode = 'sca_only' | 'always' | 'never' | 'prefer_no' | 'prefer_yes'

export const THREE_DS_MODE_DEFAULT: ThreeDSMode = 'sca_only'

const OPTIONS: Array<{ value: ThreeDSMode; label: string; hint: string }> = [
    {
        value: 'sca_only',
        label: 'SCA only (recommended)',
        hint: '3DS only where mandated (EEA + UK). Never for the US and other non-SCA countries.',
    },
    { value: 'always', label: 'Always', hint: 'Force 3DS on every transaction, regardless of country.' },
    { value: 'never', label: 'Never', hint: 'Never request 3DS (no liability shift).' },
    {
        value: 'prefer_no',
        label: 'Prefer no challenge',
        hint: 'Ask to skip the 3DS challenge when possible; the issuer may still force one.',
    },
    {
        value: 'prefer_yes',
        label: 'Prefer challenge',
        hint: 'Prefer a 3DS challenge but allow it to be skipped.',
    },
]

interface ProcessorThreeDSModeFieldProps {
    value: ThreeDSMode
    onChange: (mode: ThreeDSMode) => void
    disabled?: boolean
}

export const ProcessorThreeDSModeField = ({
    value,
    onChange,
    disabled,
}: ProcessorThreeDSModeFieldProps) => {
    const selected = OPTIONS.find((o) => o.value === value) ?? OPTIONS[0]

    return (
        <Card>
            <CardHeader>
                <CardTitle>3D Secure</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
                <Label htmlFor="threeDSMode">Authentication strategy</Label>
                <Select
                    value={value}
                    onValueChange={(v) => onChange(v as ThreeDSMode)}
                    disabled={disabled}
                >
                    <SelectTrigger id="threeDSMode" className="w-full">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {OPTIONS.map((o) => (
                            <SelectItem key={o.value} value={o.value}>
                                {o.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <p className="text-sm text-muted-foreground">{selected.hint}</p>
            </CardContent>
        </Card>
    )
}
